import React from 'react';
import ReactDOM from 'react-dom/client';
import { ConfigProvider, App as AntApp, theme as antdTheme } from 'antd';
import App from './App.jsx';
import './index.css';

// AntApp wires Ant Design's static message/notification APIs into the
// React tree so they pick up the theme and avoid the v5 deprecation warning.
//
// Theme tokens here mirror tailwind.config.js so the two systems agree on
// brand color, radius, and font family. Tailwind handles layout/spacing;
// AntD handles components.

const theme = {
  algorithm: antdTheme.defaultAlgorithm,
  token: {
    colorPrimary: '#1877F2',
    colorLink: '#1877F2',
    colorInfo: '#1877F2',
    colorSuccess: '#16A34A',
    colorWarning: '#F59E0B',
    colorError: '#DC2626',
    colorTextBase: '#0F172A',
    colorBgLayout: '#F8FAFC',
    borderRadius: 10,
    borderRadiusLG: 14,
    fontFamily:
      "Inter, system-ui, -apple-system, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif",
    fontSize: 14
  },
  components: {
    Layout: {
      headerBg: '#FFFFFF',
      siderBg: '#FFFFFF',
      bodyBg: '#F8FAFC'
    },
    Menu: {
      itemSelectedBg: '#EBF3FF',
      itemSelectedColor: '#1877F2',
      itemHoverBg: '#F1F5F9',
      itemBorderRadius: 8,
      itemHeight: 40,
      itemMarginBlock: 2,
      itemMarginInline: 6,
      itemPaddingInline: 12,
      iconSize: 16,
      collapsedIconSize: 18
    },
    Button: {
      controlHeight: 36,
      controlHeightSM: 30,
      controlHeightLG: 42,
      paddingContentHorizontal: 16,
      fontWeight: 500,
      borderRadius: 8,
      defaultBorderColor: '#E2E8F0',
      defaultHoverBorderColor: '#CBD5E1',
      defaultHoverBg: '#F8FAFC',
      defaultHoverColor: '#0F172A',
      defaultShadow: '0 1px 2px 0 rgba(15,23,42,0.04)',
      primaryShadow: '0 1px 2px 0 rgba(24,119,242,0.12)'
    },
    Input: {
      controlHeight: 40
    },
    Card: {
      borderRadiusLG: 14,
      paddingLG: 20,
      headerBg: 'transparent',
      headerFontSize: 14,
      headerHeight: 48,
      colorBorderSecondary: '#E2E8F0',
      // Refined two-layer shadow — tight 1px hairline + a soft 3px
      // diffuse lift. Matches Stripe / Linear's card depth on light
      // surfaces without becoming a drop-shadow.
      boxShadowTertiary:
        '0 1px 2px 0 rgba(15,23,42,0.04), 0 2px 6px -2px rgba(15,23,42,0.06)'
    },
    Table: {
      headerBg: '#F8FAFC',
      headerColor: '#475569',
      headerSplitColor: 'transparent',
      rowHoverBg: 'rgba(24,119,242,0.04)',
      borderColor: '#F1F5F9',
      cellPaddingBlock: 12,
      cellPaddingInline: 16,
      headerBorderRadius: 0,
      // Compact size sets a tighter rhythm so dense data tables
      // (invoices, users, custom plans) stay readable without
      // collapsing to default cramped defaults.
      cellPaddingBlockSM: 10,
      cellPaddingInlineSM: 14
    },
    Tag: {
      borderRadiusSM: 6,
      fontSizeSM: 11.5
    },
    Modal: {
      borderRadiusLG: 14,
      paddingMD: 24,
      titleFontSize: 16
    },
    Tooltip: {
      borderRadius: 6,
      paddingXS: 6,
      paddingSM: 10,
      fontSize: 12
    },
    Drawer: {
      paddingLG: 24
    },
    Notification: {
      borderRadiusLG: 12
    },
    Message: {
      borderRadiusLG: 10
    },
    Segmented: {
      itemSelectedBg: '#FFFFFF',
      itemSelectedColor: '#0F172A',
      controlPaddingHorizontal: 12,
      borderRadius: 8
    }
  }
};

ReactDOM.createRoot(document.getElementById('root')).render(
  <React.StrictMode>
    <ConfigProvider theme={theme}>
      <AntApp>
        <App />
      </AntApp>
    </ConfigProvider>
  </React.StrictMode>
);
